/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useProjectStore } from '@/ui/stores/project-store'

// Provider identifiers used across the app
export type ApiKeyProvider = 'openrouter' | 'openai' | 'anthropic' | 'google' | 'xai' | 'deepseek' | 'groq' | 'together' | 'siliconflow' | 'perplexity' | 'tavily' | 'brave' | 'serper' | 'github'

const LS_KEY = 'hivemind_apiKeys'

function readLocalStorageKeys(): Partial<Record<ApiKeyProvider, string>> {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function writeLocalStorageKeys(keys: Partial<Record<ApiKeyProvider, string>>) {
  try {
    const existing = readLocalStorageKeys()
    const merged = { ...existing, ...keys }
    // Security: Only log in development mode, never log actual keys
    if (import.meta.env.DEV) {
      console.log('[API Keys] Writing to localStorage:', {
        existing: Object.keys(existing),
        newKeys: Object.keys(keys),
        merged: Object.keys(merged),
      })
    }
    localStorage.setItem(LS_KEY, JSON.stringify(merged))
    if (import.meta.env.DEV) {
      console.log('[API Keys] Successfully wrote to localStorage')
    }
  } catch (error) {
    console.error('[API Keys] Failed to write to localStorage:', error)
  }
}

// Resolve an API key at runtime from (in order):
// 1) Current project settings (IndexedDB)
// 2) LocalStorage global fallback
// 3) Build-time env (import.meta.env)
export function getApiKey(provider: ApiKeyProvider): string | undefined {
  let fromProject: string | undefined
  try {
    const project = useProjectStore.getState().currentProject
    fromProject = project?.settings?.apiKeys?.[provider]
    if (fromProject) {
      if (import.meta.env.DEV) {
        console.log(`[API Keys] Found ${provider} in project settings`)
      }
      return fromProject
    }
  } catch (error) {
    console.warn(`[API Keys] Error reading ${provider} from project:`, error)
  }

  const fromLS = readLocalStorageKeys()[provider]
  if (fromLS) {
    if (import.meta.env.DEV) {
      console.log(`[API Keys] Found ${provider} in localStorage`)
    }
    return fromLS
  }

  // Build-time env fallbacks are disabled for security
  if (import.meta.env.DEV) {
    console.log(`[API Keys] ${provider} not found in project or localStorage`)
  }

  return undefined
}

import { UserService } from './user-service'
import { auth } from './firebase'

// Helper to merge and persist keys into the current project plus localStorage
export async function saveApiKeys(keys: Partial<Record<ApiKeyProvider, string>>) {
  writeLocalStorageKeys(keys)

  const { currentProject, updateProjectSettings } = useProjectStore.getState()

  // Also save to Firestore if user is logged in
  // Also save to Firestore if user is logged in
  const user = auth.currentUser
  console.log('[API Keys] Saving to Firestore. User:', user ? user.uid : 'null')

  if (user) {
    try {
      await UserService.saveUserPreferences(user.uid, keys)
      console.log('[API Keys] Successfully saved to Firestore')
    } catch (error) {
      console.error('[API Keys] Failed to save to Firestore:', error)
    }
  } else {
    console.warn('[API Keys] Skipping Firestore save - no user logged in')
  }

  if (!currentProject) return

  const existing = currentProject.settings?.apiKeys || {}
  const merged = { ...existing, ...keys }

  await updateProjectSettings({
    ...currentProject.settings,
    apiKeys: merged,
  })
}

// Sync keys from Firestore to LocalStorage
export async function syncKeysFromRemote(userId: string) {
  const remoteKeys = await UserService.getUserPreferences(userId)
  if (remoteKeys) {
    writeLocalStorageKeys(remoteKeys)
    return remoteKeys
  }
  return null
}

// Check if any API keys are configured
export function hasAnyApiKey(): boolean {
  // Check localStorage
  const lsKeys = readLocalStorageKeys()
  const hasLSKey = Object.values(lsKeys).some(key => key && key.trim().length > 0)
  if (hasLSKey) return true

  // Check env variables
  const providers: ApiKeyProvider[] = ['openrouter', 'openai', 'anthropic', 'google', 'xai', 'deepseek', 'groq', 'together', 'siliconflow', 'tavily', 'brave', 'serper']
  return providers.some(provider => {
    const key = getApiKey(provider)
    return key && key.trim().length > 0
  })
}

// Provider metadata for UI display and cost information
export interface ProviderInfo {
  name: string
  signupUrl: string
  costLevel: 'free' | 'ultra-low' | 'low' | 'medium' | 'high'
  costPerMTokens: string
  strengths: string
  recommended?: boolean
}

export const PROVIDER_INFO: Record<ApiKeyProvider, ProviderInfo> = {
  // Ultra-low cost providers (recommended)
  deepseek: {
    name: 'DeepSeek',
    signupUrl: 'https://platform.deepseek.com',
    costLevel: 'ultra-low',
    costPerMTokens: '$0.01-$0.05',
    strengths: 'Programming, research, reasoning',
    recommended: true
  },
  groq: {
    name: 'Groq',
    signupUrl: 'https://console.groq.com',
    costLevel: 'ultra-low',
    costPerMTokens: '$0.20',
    strengths: 'Blazing speed, Mixtral/Llama models',
    recommended: true
  },
  together: {
    name: 'Together AI',
    signupUrl: 'https://api.together.xyz/signup',
    costLevel: 'free',
    costPerMTokens: 'Free tier + $0.20',
    strengths: 'Open models (Llama, Mixtral, Gemma)',
    recommended: true
  },
  siliconflow: {
    name: 'SiliconFlow',
    signupUrl: 'https://siliconflow.com',
    costLevel: 'ultra-low',
    costPerMTokens: '$0.05-$0.086',
    strengths: 'Qwen, Llama, GLM models',
    recommended: true
  },

  // Standard providers
  openai: {
    name: 'OpenAI',
    signupUrl: 'https://platform.openai.com/signup',
    costLevel: 'medium',
    costPerMTokens: '$0.25-$2.00',
    strengths: 'GPT-4, GPT-4o, high quality'
  },
  anthropic: {
    name: 'Anthropic',
    signupUrl: 'https://console.anthropic.com',
    costLevel: 'medium',
    costPerMTokens: '$0.80-$15.00',
    strengths: 'Claude models, reasoning'
  },
  google: {
    name: 'Google AI',
    signupUrl: 'https://aistudio.google.com',
    costLevel: 'low',
    costPerMTokens: '$0.30-$2.50',
    strengths: 'Gemini models, multimodal',
    recommended: true
  },
  xai: {
    name: 'xAI (Grok)',
    signupUrl: 'https://x.ai/api',
    costLevel: 'medium',
    costPerMTokens: '$5.00-$15.00',
    strengths: 'Grok models, real-time data'
  },
  perplexity: {
    name: 'Perplexity',
    signupUrl: 'https://www.perplexity.ai/account/api/group',
    costLevel: 'low',
    costPerMTokens: '$0.20-$5.00',
    strengths: 'Sonar models with built-in web search',
    recommended: true
  },

  // Aggregator (adds markup)
  openrouter: {
    name: 'OpenRouter',
    signupUrl: 'https://openrouter.ai/keys',
    costLevel: 'high',
    costPerMTokens: 'Varies (adds markup)',
    strengths: 'Access to all models, convenience'
  },

  // Search APIs (for web search tool calling)
  tavily: {
    name: 'Tavily',
    signupUrl: 'https://tavily.com',
    costLevel: 'free',
    costPerMTokens: 'Free tier + paid',
    strengths: 'AI-optimized search, RAG workflows',
    recommended: true
  },
  brave: {
    name: 'Brave Search API',
    signupUrl: 'https://brave.com/search/api/',
    costLevel: 'low',
    costPerMTokens: '$5-$9 per 1k requests',
    strengths: 'Independent index, privacy-focused, AI grounding',
    recommended: true
  },
  serper: {
    name: 'Serper',
    signupUrl: 'https://serper.dev',
    costLevel: 'ultra-low',
    costPerMTokens: '$0.30 per 1k queries',
    strengths: 'Cheapest, Google-powered, fast',
    recommended: true
  },

  // GitHub (for repository access)
  github: {
    name: 'GitHub',
    signupUrl: 'https://github.com/settings/tokens',
    costLevel: 'free',
    costPerMTokens: 'Free',
    strengths: 'Clone and browse your repositories',
    recommended: true
  }
}

